import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { KueDifferenz } from "./auftrag/KostenuebernahmeVersionen";
import { Link } from "react-router-dom";
import { angebotsBetreff, angebotsBetreffNeutral } from "@maestro/angebots-betreff";
import { initials } from "../lib/builder-helpers";
import { istEmpfaengerDauerhaftBlockiert, mailfehlerText } from "../lib/mailfehler-anzeige";
import { abrechnungPfad, angebotsseitePfad, vorgangPfad } from "../lib/pfade";
import type { OfferOption } from "./types";
import { Aufklapper, Button, ErrorNote, Icon, Input, MenueEintrag, Modal, DateInput, Checkbox, Radio, Textarea, ButtonRouterLink } from "./components";
import { anlassAnzeige, formatDate, money } from "./format";
import { Row } from "./Row";
import { UnzustellbarDialog } from "./UnzustellbarDialog";
import { SperreVorOrt } from "./SperreVorOrt";
import type { SperrLage } from "../lib/sperren";
import { alsUnzustellbar, type UnzustellbarInfo } from "../lib/unzustellbar";
import { apiMutate } from "../hooks/useApi";
import { RUHIGER_KNOPF, RUHIGER_KNOPF_KI, TextbausteinPicker, insertAtCursor } from "./TextbausteinPicker";
import { AutomatischErkanntChip, KUNDENTYP_TEXT, KundentypAuswahl, type Kundentyp } from "./Kundentyp";
import { NACH_KARTE } from "../lib/a-la-carte";
import { DatumVergangenHinweis, vergangenesDatum } from "./DatumVergangenHinweis";

/**
 * Nennt ein (gespeicherter, ggf. bearbeiteter) Anschreiben-Text Zahlungs-/Stornobedingungen?
 * Antoine, 12.10.2026: im Text stand noch „Anzahlung von 20 %, Zahlungsziel 5 Tage", obwohl das
 * Angebot längst 100 % / 10 Tage und neue Stornobedingungen trug. Der Entwurf wird nicht
 * ungefragt überschrieben (er kann von Hand bearbeitet sein) — der Dialog weist nur darauf hin.
 */
export function nenntKonditionen(text: string): boolean {
  return /anzahlung|restzahlung|zahlungsziel|zahlungsfrist|storn|deposit|cancellation|payment term/i.test(text);
}

/**
 * Angebot senden — 5a (Compose) / 5b (Erfolg) im selben Dialog, dritter Zustand "mail_failed"
 * (H-13: versioniert, aber E-Mail fehlgeschlagen). Aus `builder.tsx` (Schritt 8, Einheit C,
 * Konzept § 2.10/§ 4.8/§ 6.5 — hoechstes Versandrisiko im gesamten Refactoring).
 *
 * WAS HIER LOKAL IST (Phase B) und was bewusst NICHT gewandert ist:
 * - `sendPhase`/`ccSelf`/`includeOffer`/`freetextText`/`sentResult`/`sendMailError`/
 *   `freetextSentAt`/`sending`/`copyDone` + die beiden Textarea-Refs sind jetzt reiner,
 *   dialog-lokaler UI-State (Konzept § 3 Tabelle Zeile 3 nennt genau diese Beispiele).
 *   `sendPhase` wird bei jedem Uebergang `open: false -> true` per `useLayoutEffect` auf
 *   "compose" zurueckgesetzt (`useLayoutEffect`, NICHT `useEffect`, damit kein Frame lang der
 *   alte mail_failed/sent-Zustand aufblitzt, bevor der Reset greift — reproduziert exakt, was
 *   `openSend()` zuvor synchron im selben Klick erledigte). Genau dieses Timing-Detail ist der
 *   Grund, warum NICHT alles aus dem Konzept-Beispiel wandert, siehe naechster Punkt.
 * - `sendOpen` (hier `open`/`onClose`), `subject`, `letter`, `letterBusy` bleiben BEWUSST beim
 *   Parent (`builder.tsx`), abweichend vom Konzept-Beispiel in § 3, das `letter`/`subject` als
 *   Beispiel fuer wandernden State nennt: `sendOpen` muss beim Parent bleiben, weil zwei externe
 *   Stellen ausserhalb dieses Dialogs ihn oeffnen (Desktop- und Mobil-"Senden vorbereiten"-Knopf,
 *   exakt dieselbe Begruendung wie `positionDrawerOpen` bei `PositionHinzufuegenDrawer`,
 *   Schritt 7). `openSend()` selbst bleibt im Parent, weil es beim OEFFNEN synchron das
 *   KI-Anschreiben nachlaedt (`generate-letter`) und `subject`/`letter` befuellt — ein
 *   `useEffect`-Bruecken-Muster dafuer haette den synchronen Charakter dieses Ladevorgangs in
 *   einen asynchronen Nach-dem-Rendern-Effekt verwandelt und war nicht mit Sicherheit als
 *   verhaltensgleich zu belegen (siehe Bericht der Iteration). `letter`/`subject`/`letterBusy`
 *   bleiben deshalb als kontrollierter State beim Parent, hier nur als Props durchgereicht —
 *   `openSend()`/`regenerateLetter()` sind dadurch UNVERAENDERT geblieben.
 * - `sendOffer()` bleibt beim Parent (fachlicher State: `save()`-Flush/H-09/H-10,
 *   `offer-letter`-PATCH/H-13, `offersQ.reload()`, `id`) — exakt dasselbe Muster wie
 *   `applyPackage`/`onApply` (Schritt 6) und `parseFreeform`/`onParseFreeform` (Schritt 7). Die
 *   Signatur wurde umgebaut: statt `letter`/`subject`/`ccSelf`/`includeOffer`/`freetextText` per
 *   Closure zu lesen, nimmt sie jetzt einen `SendPayload` entgegen (die drei jetzt lokalen
 *   Felder) und GIBT das Ergebnis zurueck (`SendOutcome | null`), statt `sendPhase`/`sentResult`/
 *   `sendMailError`/`freetextSentAt` selbst zu setzen — `handleSend()` unten uebernimmt das
 *   Ergebnis in den lokalen State, `sending` wird dabei in einem `try {...} finally {...}` um
 *   den Aufruf gesetzt (exaktes Muster von `PaketUebernehmenPanel.handleApply`, Schritt 6). Die
 *   Reihenfolge flush -> Anschreiben-Save -> Versand-API-Call innerhalb von `sendOffer()` selbst
 *   ist UNVERAENDERT (nur die Ein-/Ausgabe wurde von Closures auf Parameter/Rueckgabewert
 *   umgestellt, kein Schritt wurde umsortiert, entfernt oder ergaenzt).
 * - `preview()`/`patchValidUntil()` bleiben beim Parent (werden auch ausserhalb dieses Dialogs
 *   gebraucht, siehe `builder.tsx`) und werden unveraendert als Callback-Props durchgereicht.
 * - `copyPublicLink()` ist vollstaendig hierher gewandert (rein lokal: Zwischenablage + eigener
 *   `copyDone`-Blink, keine Abhaengigkeit von Parent-State).
 * - `error` bleibt Seiten-globaler State beim Parent (wird an ZWEI Stellen angezeigt — Seite UND
 *   Dialog, siehe `builder-versandmodus.test.tsx`) und kommt nur lesend als Prop herein.
 */

export interface SendPayload {
  /** true = Angebot mitschicken (als LINK auf die Angebotsseite) -> Varianten-Pfad;
   *  false = reine Nachricht -> Freitext-Pfad.
   *  Ersetzt den frueheren `sendMode`-Umschalter (22.08.2026): der Betreiber denkt in
   *  "Angebot dranhaengen ja/nein", nicht in zwei abstrakten Versandmodi.
   *
   *  B5 (UX-Polish 25.08.2026): Der Name bleibt, die Beschreibung nicht — hier stand
   *  "(Link + PDF)". Es wird KEIN PDF angehaengt: keine der `mailer.send`-Aufrufstellen
   *  uebergibt `attachments`, und ein serverseitiges PDF existiert ueberhaupt nicht
   *  (`GET /api/offers/:id/document` liefert druckfertiges HTML, die HTML-PDF-Wandlung ist laut
   *  Kommentar in `routes/offer-items.ts` "ein spaeterer Adapter"). Die Mail traegt den Link auf
   *  die Angebotsseite; dort kann die Kundin drucken (siehe `pages/offer-public.tsx`). */
  includeOffer: boolean;
  ccSelf: boolean;
  freetextText: string;
  /**
   * B3 (UX-Polish 25.08.2026, selektiver Versand): Die Varianten, die der Betreiber im Dialog
   * ABGEWAEHLT hat. `builder.tsx` deaktiviert genau diese vor dem Versand
   * (`PATCH /api/offers/:id` `{isActive:false}`) — der Server stellt das versendbare Angebot
   * danach aus den verbleibenden aktiven Varianten zusammen.
   *
   * WARUM ERST BEIM SENDEN und nicht schon beim Klick auf die Checkbox: `options` enthaelt nur
   * AKTIVE Varianten (`builder.tsx`). Ein sofortiges PATCH liesse die abgewaehlte Karte aus dem
   * Dialog verschwinden — der Haken waere nicht mehr zurueckzunehmen, und ein Fehlklick haette
   * eine Variante stillschweigend soft-geloescht. Bis „Jetzt senden" bleibt die Abwahl deshalb
   * dialog-lokal und damit umkehrbar; erst der Versand macht sie wirklich.
   */
  deaktivierteOptionIds: string[];
  /**
   * Neuversand-Schutz (20.10.2026): gesetzt nur aus dem Dialog „Erneut senden?“ bei vollem
   * Postfach (`POST /offers/send` `{ trotzdem: true }`). Der Server prüft dieselbe Regel nochmal.
   */
  trotzdem?: boolean;
  /** V-1304: der Betreiber hat bestätigt, dass das Datum in der Vergangenheit stimmt
   *  (`POST /offers/send` `{ datumBestaetigt: true }`, sonst 409 `datum_in_vergangenheit`). */
  datumBestaetigt?: boolean;
}

/**
 * Antoine (12.10.2026): „Ich muss die Möglichkeit haben, das Angebot zu schicken oder die
 * Kostenübernahme oder beides." Bis dahin ging die Kostenuebernahme nur ZUSAETZLICH zur
 * Angebots-Mail raus. "kostenuebernahme" ruft allein denselben Endpunkt wie die Abrechnungsseite
 * (`POST /api/events/:id/cost-acceptance`, ohne Zustandssperre) — keine Angebots-Mail, keine
 * neue Angebotsversion.
 */
export type VersandArt = "angebot" | "kostenuebernahme" | "beides";

export interface SendOutcome {
  phase: "sent" | "mail_failed";
  mailError: string | null;
  sentResult?: { publicToken: string; version: number; sentAt: string };
  freetextSentAt?: string;
}

export function AngebotSendenDialog({
  open,
  onClose,
  eventId,
  eventOccasion,
  eventDateIso,
  eventDateEndIso,
  eventGuestCount,
  eventOfferLanguage,
  eventNumber,
  eventOfferValidUntil,
  eventPublicToken,
  customerId,
  customerName,
  customerEmail,
  companyEmail,
  options,
  activeId,
  aktiverBetragCents,
  error,
  subject,
  onSubjectChange,
  letter,
  onLetterChange,
  letterBusy,
  onRegenerateLetter,
  letterErzeugt = null,
  vorherigesAnschreiben = null,
  onVorherigesAnschreibenWiederherstellen,
  letterRefineBusy,
  onRefineLetter,
  onRequestCostAcceptance,
  kundentypUnbestaetigt,
  paymentPathway,
  onSend,
  sperre = null,
  onSperreNeuLaden,
  onPreview,
  onPatchValidUntil,
  onPatchEventDate,
  offenePunkte,
  angebotGesperrt,
  freischaltenKarte = null,
  nachtrag = false,
}: {
  open: boolean;
  onClose: () => void;
  eventId: string | undefined;
  eventOccasion: string | null | undefined;
  eventDateIso: string | null | undefined;
  /** Enddatum einer mehrtägigen Veranstaltung (`dateEnd`); die Vergangenheits-Prüfung gilt dem Ende, wie auf dem Server. */
  eventDateEndIso?: string | null;
  eventGuestCount: number | null | undefined;
  /** Punkt (b), 27.09.2026: fuer den neutralen Betreff beim Freitext-Versand ohne Angebot noetig
   *  (`angebotsBetreffNeutral()`), dieselbe Sprache wie der Angebots-Betreff (`event.offerLanguage`). */
  eventOfferLanguage?: "de" | "en" | null;
  eventNumber: string | null | undefined;
  eventOfferValidUntil: string | null | undefined;
  eventPublicToken: string | null | undefined;
  /** Nur fuer den Weg zu den Kundendaten gebraucht (dauerhaft blockierter Empfaenger, s. unten). */
  customerId: string | null | undefined;
  customerName: string | null | undefined;
  customerEmail: string | null | undefined;
  companyEmail: string | null | undefined;
  options: OfferOption[];
  activeId: string | null;
  /**
   * Der LIVE gerechnete Gesamtbetrag der gerade bearbeiteten Variante (`breakdown.amountTotalCents`
   * aus `builder.tsx`) — dieselbe Zahl, die die Zusammenfassung rechts zeigt. Ohne sie nennt
   * dieser Dialog den Stand vom Seitenaufruf; die Begruendung steht an der Verwendungsstelle.
   * Optional, damit Aufrufer ohne Editor-Kontext (Tests, kuenftige Einstiege) unveraendert laufen.
   */
  aktiverBetragCents?: number | null;
  error: string | null;
  /** S6: eine erwartbare Sperre beim Senden (409 u. ä.) — Satz + Ausweg direkt am Senden-Knopf statt „Etwas ist schiefgelaufen“. */
  sperre?: SperrLage | null;
  /** „Neu laden“ am Sperr-Hinweis (die Seite holt den Stand frisch); der Dialog schließt danach. */
  onSperreNeuLaden?: () => void;
  subject: string;
  onSubjectChange: (v: string) => void;
  letter: string;
  onLetterChange: (v: string) => void;
  letterBusy: boolean;
  /** Bekommt immer die aktuelle Abwahl (`excludedOptionIds` der Route generate-letter) — der
   *  Text soll nur die Varianten nennen, die tatsaechlich mitgehen. */
  onRegenerateLetter: (excludedOptionIds: string[]) => void | Promise<void>;
  /** 20.10.2026: der zuletzt maschinell erzeugte Text. Weicht `letter` davon ab, hat der Betreiber
   *  von Hand geaendert — dann wird bei geaenderter Variantenauswahl nichts ueberschrieben, es
   *  gibt nur einen Hinweis. `null` (unbekannt) gilt vorsichtshalber als von Hand geaendert. */
  letterErzeugt?: string | null;
  /** Antoine 13.10.2026: von Hand bearbeiteter Text, der beim Oeffnen ersetzt wurde, weil sich das
   *  Angebot geaendert hat. Gesetzt -> Hinweis mit „Vorherigen Text wiederherstellen". */
  vorherigesAnschreiben?: string | null;
  onVorherigesAnschreibenWiederherstellen?: () => void;
  /** Punkt 7 (Bughunt 27.08.2026): "Text ueberarbeiten" - leichte Korrektur des vorhandenen
   *  Textes statt Neugenerierung. Eigener Ladezustand, getrennt von `letterBusy`, damit beide
   *  Knoepfe waehrend des jeweils ANDEREN laufenden Vorgangs mitsperren, aber die richtige
   *  Beschriftung zeigen. */
  letterRefineBusy: boolean;
  onRefineLetter: () => void;
  /**
   * Punkt 8 (Bughunt 27.08.2026): "Kostenübernahme mit anfordern" beim Angebots-Versand. Ruft
   * denselben Endpunkt wie die Abrechnungsseite auf (`POST /api/events/:id/cost-acceptance`),
   * NICHT ein zusammengelegter Mail-Versand — zwei unabhaengige, je eigene E-Mails. `personalNote`
   * ist der aktuelle Anschreiben-Text (`letter`), damit die Nachricht nicht zweimal getippt
   * werden muss. Gibt das Ergebnis zurueck statt zu werfen: ein Fehlschlag hier darf das bereits
   * gesendete Angebot nicht verdecken (kein throw, keine zweite Fehlerquelle fuer denselben Dialog).
   */
  onRequestCostAcceptance: (personalNote: string, kundentyp?: Kundentyp) => Promise<{ ok: true } | { ok: false; message: string }>;
  /**
   * Punkt 42 (Geld-Zweitpruefung): der nur automatisch erkannte Kundentyp (null = bestaetigt).
   * Der Server verlangt dann beim Anfordern eine Bestaetigung (409 `kundentyp_unbestaetigt`) —
   * ohne diese Auswahl liefe der Haken fuer jeden noch nicht bestaetigten Kunden in den Fehler.
   */
  kundentypUnbestaetigt?: Kundentyp | null;
  /** Zahlungsweg des Angebots (`payment-terms` effective). Bei "kostenuebernahme" ist "Beides"
   *  vorbelegt — gesendet wird trotzdem erst auf den Klick. */
  paymentPathway?: "stripe" | "kostenuebernahme" | null;
  onSend: (payload: SendPayload) => Promise<SendOutcome | null>;
  onPreview: (path: string) => void;
  onPatchValidUntil: (value: string) => void;
  /** V-1304: übernimmt den Vorschlag aus dem Hinweis „Datum liegt in der Vergangenheit“ (ISO). Bei
   *  mehrtägigen Veranstaltungen kommt das verschobene Ende als zweites Argument (ein PATCH mit beiden
   *  Feldern). Liefert `false`, wenn das Speichern scheiterte — dann bleibt der Betreff unverändert. */
  onPatchEventDate?: (iso: string, isoEnde?: string) => void | boolean | Promise<void | boolean>;
  /**
   * B1 (UX-Polish 25.08.2026): Die noch offenen Sende-Reife-Punkte, beim Namen
   * („Rückfragen beantwortet", „Alle Positionen bepreist", …). Sie kommen aus `sendeReifeItems`
   * in `builder.tsx` — derselbe Rechenweg, der den CTA einfaerbt; hier nur die Beschriftungen
   * der unerledigten.
   *
   * WARUM SIE IN DIESEN DIALOG GEHOEREN: bis heute zeigte sie die Karte „Bereit zum Senden" auf
   * der Seite dahinter. Die ist entfallen — und der Dialog verdeckt die Seite. Ohne diese Zeilen
   * waere der letzte Bildschirm vor dem Versand ausgerechnet der einzige, auf dem NICHT steht,
   * dass zwei Rueckfragen der Kundin unbeantwortet sind. Genau hier ist ein Abbruch noch gratis.
   */
  offenePunkte: string[];
  /**
   * B3: Ist der Vorgang nach verbindlicher Annahme gesperrt (`offer_chosen` und aufwaerts,
   * `lib/offer-lock.ts`)? Dann laesst der Server das Abwaehlen nur durch ein sehr enges
   * Nadeloehr — und die Oberflaeche muss es RESPEKTIEREN, nicht dagegenlaufen: eine gesperrte
   * Checkbox mit Begruendung ist ehrlicher als ein Haken, der in einen 409 laeuft.
   */
  angebotGesperrt: boolean;
  /** Ruhige Hinweiskarte mit Freischalt-Knopf, solange das Angebot gesperrt ist (statt roter Fehlermeldung beim Senden). */
  freischaltenKarte?: ReactNode;
  /** Nachtrag-Version: Versand eines Nachtrags zu einem angenommenen Auftrag (kein Anschreiben, fester Betreff). */
  nachtrag?: boolean;
}) {
  const [sendPhase, setSendPhase] = useState<"compose" | "sent" | "mail_failed">("compose");
  const [sendMailError, setSendMailError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  /** V-1304: „Datum stimmt so“ — gilt nur für das Datum, das gerade angezeigt wird. */
  const [datumBestaetigtFuer, setDatumBestaetigtFuer] = useState<string | null>(null);
  // Neuversand-Schutz (20.10.2026): 409 `adresse_unzustellbar` -> Dialog mit neuer Adresse.
  const [unzustellbar, setUnzustellbar] = useState<UnzustellbarInfo | null>(null);
  const [ccSelf, setCcSelf] = useState(true);
  const [sentResult, setSentResult] = useState<{ publicToken: string; version: number; sentAt: string } | null>(null);
  const [includeOffer, setIncludeOffer] = useState(true);
  const [freetextText, setFreetextText] = useState("");
  const [freetextSentAt, setFreetextSentAt] = useState<string | null>(null);
  const [copyDone, setCopyDone] = useState(false);
  /** B3: dialog-lokale Abwahl, siehe `SendPayload.deaktivierteOptionIds`. */
  const [abgewaehlt, setAbgewaehlt] = useState<Set<string>>(() => new Set());
  /** Punkt 8: Haken "Kostenübernahme mit anfordern" + Ergebnis des zweiten, unabhaengigen
   *  Requests nach dem Versand (fuer die Erfolgs-/Fehleranzeige, s. unten). */
  const vorbelegteVersandArt = (): VersandArt => (paymentPathway === "kostenuebernahme" ? "beides" : "angebot");
  const [versandWahl, setVersandWahl] = useState<VersandArt>(vorbelegteVersandArt);
  const [caOutcome, setCaOutcome] = useState<{ ok: boolean; message?: string } | null>(null);
  const [kundentypWahl, setKundentypWahl] = useState<Kundentyp | null>(null);
  /**
   * 20.10.2026 (WvonZ-Durchtest): das Anschreiben folgt der Variantenauswahl. `auswahlBasis` ist die
   * Abwahl (als Schluessel), zu der der Text zuletzt erzeugt wurde bzw. bei Dialog-Oeffnung galt.
   * Aendert sich die Abwahl danach, wird ein unveraenderter KI-Text nach 600 ms neu formuliert,
   * ein von Hand geaenderter nur mit einem Hinweis versehen.
   */
  const auswahlBasis = useRef("");
  const auswahlTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [auswahlHinweis, setAuswahlHinweis] = useState<"auto" | "hand" | null>(null);
  const letterTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const freetextTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  /** Punkt (b), 27.09.2026: `true`, solange der Betreff noch der zuletzt AUTOMATISCH gesetzte ist
   *  (von `openSend()` in `builder.tsx` oder vom Toggle unten). Das Betreff-Textfeld setzt ihn auf
   *  `false`, sobald der Betreiber selbst tippt — Antoines Vorgabe: "Hat der Betreiber den Betreff
   *  selbst geaendert, darf ein Umschalten des Haekchens seinen Text nicht ueberschreiben." */
  const subjectAutomatisch = useRef(true);
  const vorherigesIncludeOffer = useRef(includeOffer);

  // Punkt (b): "Angebot beifuegen" umgeschaltet -> Betreff zwischen `angebotsBetreff()` (mit
  // Angebot) und `angebotsBetreffNeutral()` (ohne, "Ihre Anfrage fuer den …") wechseln — aber NUR
  // wenn der Betreff noch der automatische ist. `vorherigesIncludeOffer` verhindert, dass der
  // Effekt beim ERSTEN Rendern (Mount, kein echter Toggle) den noch leeren/eigenen Betreff
  // ueberschreibt: `includeOffer` "aendert" sich beim Mount ja nicht wirklich.
  useEffect(() => {
    if (vorherigesIncludeOffer.current === includeOffer) return;
    vorherigesIncludeOffer.current = includeOffer;
    if (!subjectAutomatisch.current) return;
    const lang = eventOfferLanguage ?? "de";
    const neu = includeOffer
      ? angebotsBetreff({ lang, eventDate: eventDateIso, optionGuestCounts: options.map((o) => o.guestCount), guestCount: eventGuestCount, occasion: eventOccasion, kundenName: customerName })
      : angebotsBetreffNeutral({ lang, eventDate: eventDateIso });
    onSubjectChange(neu);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur auf den Toggle reagieren, nicht auf jede Datumsaenderung
  }, [includeOffer]);

  // Reproduziert `openSend()`s bisheriges `setSendPhase("compose")` bei jedem Oeffnen -- als
  // `useLayoutEffect` (nicht `useEffect`), damit vor dem naechsten Bildschirmaufbau bereits der
  // richtige Zustand steht und kein alter mail_failed/sent-Screen kurz aufblitzt.
  // B3: Die Abwahl wird beim selben Uebergang zurueckgesetzt. Ein zweites Oeffnen darf nicht
  // stillschweigend eine Abwahl von vorhin mitschleppen -- das waere genau die Art
  // unsichtbarer Zustand, die zu einem versehentlich unvollstaendigen Angebot fuehrt.
  useLayoutEffect(() => {
    if (open) {
      setSendPhase("compose");
      setAbgewaehlt(new Set());
      auswahlBasis.current = "";
      setAuswahlHinweis(null);
      if (auswahlTimer.current) clearTimeout(auswahlTimer.current);
      setVersandWahl(vorbelegteVersandArt());
      setCaOutcome(null);
      setKundentypWahl(kundentypUnbestaetigt ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur beim Oeffnen vorbelegen
  }, [open]);

  /**
   * B3 — die Grenzen, die der Server zieht, hier als Anzeige-Regel nachgebaut.
   *
   * `PATCH /api/offers/:id` laesst das Deaktivieren im gesperrten Zustand nur durch ein enges
   * Nadeloehr (`routes/offers.ts`, belegt in `test/offer-lock.test.ts`):
   *   * `{isActive:false}` muss das EINZIGE Feld sein  -> erfuellt `builder.tsx` beim Senden,
   *   * die Variante darf NICHT die gewaehlte sein     -> `o.isChosen` unten,
   *   * es muss mindestens eine aktive uebrig bleiben  -> `letzteVerbleibende` unten
   *     (serverseitig 422 `last_option_protected`, gilt gesperrt wie ungesperrt).
   * Ausserdem faellt ein Wieder-Aktivieren (`{isActive:true}`) im gesperrten Zustand unter die
   * Sperre — deshalb ist dort auch das Zuruecknehmen einer Abwahl nicht erlaubt; unsere Abwahl
   * ist bis zum Versand ohnehin nur lokal, also stellt sich die Frage praktisch nicht.
   *
   * Warum die UI das nachbaut statt es auf den Fehler ankommen zu lassen: ein Haken, der beim
   * Absenden mit 409/422 zurueckkommt, hat den Betreiber schon glauben lassen, es ginge. Eine
   * gesperrte Checkbox mit Begruendung sagt es ihm vorher.
   */
  const aktiveNachAbwahl = options.filter((o) => !abgewaehlt.has(o.id)).length;

  // Variantenauswahl geaendert -> Anschreiben nachziehen (siehe `auswahlBasis`).
  const auswahlSchluessel = [...abgewaehlt].sort().join(",");
  const letterAktuell = useRef(letter);
  letterAktuell.current = letter;
  useEffect(() => {
    if (!open || !includeOffer) return;
    if (auswahlSchluessel === auswahlBasis.current) {
      setAuswahlHinweis(null);
      return;
    }
    if (!letterAktuell.current.trim()) {
      auswahlBasis.current = auswahlSchluessel; // noch kein Text -> nichts nachzuziehen
      return;
    }
    if (letterAktuell.current !== letterErzeugt) {
      setAuswahlHinweis("hand");
      return;
    }
    setAuswahlHinweis("auto");
    const ids = [...abgewaehlt];
    const schluessel = auswahlSchluessel;
    auswahlTimer.current = setTimeout(() => {
      auswahlBasis.current = schluessel;
      void Promise.resolve(onRegenerateLetter(ids)).finally(() => {
        if (auswahlBasis.current === schluessel) setAuswahlHinweis(null);
      });
    }, 600);
    return () => {
      if (auswahlTimer.current) clearTimeout(auswahlTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur auf die Auswahl reagieren
  }, [auswahlSchluessel, open, includeOffer]);
  function neuFormulieren() {
    if (auswahlTimer.current) clearTimeout(auswahlTimer.current);
    auswahlBasis.current = auswahlSchluessel;
    setAuswahlHinweis(null);
    void onRegenerateLetter([...abgewaehlt]);
  }
  /**
   * Punkt 8: die Kostenuebernahme braucht serverseitig eine EINDEUTIGE Variante als
   * Vertragsgrundlage (`verbindlicheVariante()`, `routes/cost-acceptance.ts` — 422
   * `no_chosen_option` bei mehreren aktiven ohne Wahl). Dieselbe Bedingung hier client-seitig
   * nachgebaut (wie `abwahlGesperrtGrund` oben), damit der Haken erst gar nicht anklickbar ist,
   * wenn der Versand mehrere aktive Varianten mitschickt. Zusaetzlich nur bei `includeOffer`
   * sinnvoll (Antoines Wortlaut: "wenn ich ... ein neues Angebot ... sende").
   */
  const kostenuebernahmeMoeglich = includeOffer && aktiveNachAbwahl === 1;
  /** Nur Kostenuebernahme: ohne Angebots-Versand wirkt eine Abwahl nicht (sie greift erst in
   *  `onSend`), also zaehlt der echte Stand — eine einzige aktive oder eine gewaehlte Variante. */
  const kostenuebernahmeAlleinMoeglich = options.length === 1 || options.some((o) => o.isChosen);
  /** Die Wahl gilt nur, solange sie moeglich ist — sonst zeigt (und sendet) der Dialog "Angebot".
   *  So kann nie etwas rausgehen, das der markierte Radio-Knopf nicht sagt. */
  const versandArt: VersandArt =
    (versandWahl === "beides" && !kostenuebernahmeMoeglich) || (versandWahl === "kostenuebernahme" && !kostenuebernahmeAlleinMoeglich) ? "angebot" : versandWahl;
  const nurKostenuebernahme = versandArt === "kostenuebernahme";
  // Gesperrtes Angebot: nur das Angebot selbst ist blockiert; eine reine Nachricht (Haken aus) geht weiter.
  const versandBlockiert = angebotGesperrt && includeOffer && !nurKostenuebernahme;
  // V-1304: Datum in der Vergangenheit — Versand erst nach Korrektur oder ausdrücklicher Bestätigung.
  const datumVergangen = includeOffer && !nurKostenuebernahme && vergangenesDatum(eventDateIso, undefined, eventDateEndIso) != null;
  const datumSchluessel = `${(eventDateIso ?? "").slice(0, 10)}|${(eventDateEndIso ?? "").slice(0, 10)}`;
  const datumBestaetigt = datumVergangen && datumBestaetigtFuer === datumSchluessel;
  // Datum übernommen -> der AUTOMATISCHE Betreff bekommt das neue Datum, ein selbst getippter bleibt.
  async function korrigiereDatum(iso: string, isoEnde?: string | null) {
    if (!onPatchEventDate) return;
    const ergebnis = await (isoEnde ? onPatchEventDate(iso, isoEnde) : onPatchEventDate(iso));
    if (ergebnis === false || nachtrag || !subjectAutomatisch.current) return;
    const lang = eventOfferLanguage ?? "de";
    onSubjectChange(
      includeOffer
        ? angebotsBetreff({ lang, eventDate: iso, optionGuestCounts: options.map((o) => o.guestCount), guestCount: eventGuestCount, occasion: eventOccasion, kundenName: customerName })
        : angebotsBetreffNeutral({ lang, eventDate: iso }),
    );
  }
  // 25.10.2026 (Antoine, V-1273): „Ich schreibe und die KI überarbeitet dann.“ Das KI-Menü ist immer
  // da; „Text überarbeiten“ wirkt auf das Feld, das gerade sichtbar ist. Mit mitgeschicktem Angebot
  // bleibt der bisherige Weg (`onRefineLetter`, Elternseite). Sonst (reine Nachricht, Kostenübernahme)
  // überarbeitet der Dialog selbst und schreibt NICHTS in den gespeicherten Anschreiben-Entwurf
  // (`persist: false`) — der Text steht nur im Feld, bis gesendet wird.
  const [refineLokalBusy, setRefineLokalBusy] = useState(false);
  const [refineLokalFehler, setRefineLokalFehler] = useState<string | null>(null);
  const elternRefine = includeOffer && !nurKostenuebernahme;
  const kiBusy = letterBusy || letterRefineBusy || refineLokalBusy;
  const kiBusyText = letterRefineBusy || refineLokalBusy ? "Überarbeitet …" : letterBusy ? "Erstellt …" : null;
  const feldText = includeOffer || nurKostenuebernahme ? letter : freetextText;
  async function textUeberarbeiten() {
    if (elternRefine) {
      onRefineLetter();
      return;
    }
    if (!feldText.trim()) return;
    setRefineLokalBusy(true);
    setRefineLokalFehler(null);
    try {
      const res = await apiMutate<{ letter: string }>(`/api/events/${eventId}/offers/refine-letter`, "POST", { text: feldText, persist: false });
      const neu = res?.letter;
      if (neu) {
        if (includeOffer || nurKostenuebernahme) onLetterChange(neu);
        else setFreetextText(neu);
      }
    } catch (e) {
      setRefineLokalFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setRefineLokalBusy(false);
    }
  }
  const costAcceptanceGewuenscht = versandArt !== "angebot";
  const versandWahlSichtbar = kostenuebernahmeMoeglich || kostenuebernahmeAlleinMoeglich;
  /** Textfeld/Erfolgsansicht: wie die reine Nachricht ohne Angebotsseite. */
  const ohneAngebotsseite = !includeOffer || nurKostenuebernahme;
  const KOSTENUEBERNAHME_NOTE_MAX = 2000;
  const letterZuLangFuerKostenuebernahme = letter.trim().length > KOSTENUEBERNAHME_NOTE_MAX;
  function abwahlGesperrtGrund(o: OfferOption): string | null {
    if (abgewaehlt.has(o.id)) return null; // Zuruecknehmen ist immer erlaubt (rein lokal).
    if (o.isChosen) return "Diese Variante hat die Kundin verbindlich gewählt — sie bleibt im Angebot.";
    if (angebotGesperrt && options.length <= 1) return "Das Angebot ist nach der Annahme gesperrt.";
    if (aktiveNachAbwahl <= 1) return "Mindestens eine Variante muss mitgehen — ein Angebot ohne Variante ist keins.";
    return null;
  }

  async function copyPublicLink(token: string) {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/angebot/${token}`);
      setCopyDone(true);
      setTimeout(() => setCopyDone(false), 2000);
    } catch {
      /* Zwischenablage nicht verfuegbar -- kein hartes Fehlverhalten noetig. */
    }
  }

  async function handleSend() {
    setSending(true);
    try {
      if (nurKostenuebernahme) {
        if (letterZuLangFuerKostenuebernahme) return;
        const ca = kundentypUnbestaetigt
          ? await onRequestCostAcceptance(letter, kundentypWahl ?? kundentypUnbestaetigt)
          : await onRequestCostAcceptance(letter);
        setCaOutcome(ca);
        if (ca.ok) setSendPhase("sent");
        return;
      }
      await sendeAngebot({}, false);
    } finally {
      setSending(false);
    }
  }

  /**
   * Der Versand des Angebots samt Übernahme des Ergebnisses. Antwortet der Server mit 409
   * `adresse_unzustellbar` (Neuversand-Schutz, 20.10.2026), öffnet sich der Dialog „Erneut senden?“;
   * `ausDialog` heißt, der Aufruf kommt aus genau diesem Dialog — dort werden Fehler weitergereicht,
   * damit er sie anzeigt, statt sich erneut zu öffnen.
   */
  async function sendeAngebot(optionen: { trotzdem?: boolean }, ausDialog: boolean) {
    let result: SendOutcome | null;
    try {
      result = await onSend({
        includeOffer,
        ccSelf,
        freetextText,
        deaktivierteOptionIds: [...abgewaehlt],
        ...(optionen.trotzdem ? { trotzdem: true } : {}),
        ...(datumBestaetigt ? { datumBestaetigt: true } : {}),
      });
    } catch (e) {
      const u = alsUnzustellbar(e);
      if (u && !ausDialog) {
        setUnzustellbar(u);
        return;
      }
      throw e;
    }
    if (!result) return;
    setSendMailError(result.mailError);
    setSendPhase(result.phase);
    if (result.sentResult) setSentResult(result.sentResult);
    if (result.freetextSentAt) setFreetextSentAt(result.freetextSentAt);
    /**
     * Punkt 8: die Kostenuebernahme-Anforderung laeuft ERST NACH einem erfolgreich versendeten
     * Angebot (`phase === "sent"`) — bei `mail_failed` waere ein zweiter, unabhaengiger
     * Rechtsdokument-Versand verwirrend, solange die eigentliche Angebots-Mail nicht einmal
     * ankam. Ein Fehlschlag hier ueberschreibt NICHT `sendMailError`/`sendPhase` - das Angebot
     * bleibt "gesendet", nur `caOutcome` traegt den zusaetzlichen, getrennten Befund.
     */
    if (result.phase === "sent" && costAcceptanceGewuenscht && kostenuebernahmeMoeglich && !letterZuLangFuerKostenuebernahme) {
      const ca = kundentypUnbestaetigt
        ? await onRequestCostAcceptance(letter, kundentypWahl ?? kundentypUnbestaetigt)
        : await onRequestCostAcceptance(letter);
      setCaOutcome(ca);
    }
  }

  /**
   * Hat der Mailprovider die Empfaengeradresse DAUERHAFT abgewiesen (Cloudflare-Suppression,
   * Fehler 12206)? Dann ist „E-Mail erneut senden" keine Hilfe, sondern eine Endlosschleife: die
   * Adresse steht auf einer Sperrliste, jeder weitere Versuch wird identisch abgewiesen. Der
   * Knopf verschwindet deshalb in genau diesem Fall — und NUR in diesem; jeder andere Mailfehler
   * behaelt die Wiederholung unveraendert (fluechtige Fehler sind der Normalfall).
   *
   * WAS AN SEINE STELLE TRITT und warum nicht bloss „Schliessen": die einzige Handlung, die die
   * Lage aufloest, ist eine ANDERE Empfaengeradresse. Die steht in den Kundendaten, dort ist sie
   * auch editierbar (`/kunden/:id`, Karte „Bearbeiten"). Der Dialog fuehrt also genau dorthin,
   * statt den Betreiber mit „Schliessen" allein zu lassen und ihn die Adresse selbst suchen zu
   * lassen. Fehlt ausnahmsweise die `customerId` (Vorgang ohne verknuepften Kunden), bleibt es
   * beim blossen Schliessen — ein Link ins Leere waere schlechter als keiner.
   */
  const empfaengerBlockiert = istEmpfaengerDauerhaftBlockiert(sendMailError);
  const kundendatenPfad = customerId ? `/kunden/${customerId}` : null;

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      width={sendPhase === "compose" ? "600px" : "468px"}
      title={
        sendPhase === "mail_failed" && !includeOffer
          ? "Nachricht NICHT versendet"
          : sendPhase === "mail_failed" && sentResult
            ? "Angebot gespeichert – E-Mail NICHT versendet"
            : sendPhase === "compose"
              ? nurKostenuebernahme
                ? `Kostenübernahme an ${customerName ?? "Kunde"} senden`
                : `Angebot an ${customerName ?? "Kunde"} senden`
              : sendPhase === "sent" && nurKostenuebernahme
                ? "Kostenübernahme angefordert"
                : sendPhase === "sent" && !includeOffer
                ? "Mail wurde versendet"
                : "Angebot gesendet"
      }
      subtitle={
        sendPhase === "compose"
          ? [anlassAnzeige(eventOccasion), eventDateIso ? formatDate(eventDateIso) : null, eventGuestCount ? `${eventGuestCount} Gäste` : null, eventNumber ? `Angebot ${eventNumber}` : null]
              .filter(Boolean)
              .join(" · ")
          : undefined
      }
      footer={
        sendPhase === "mail_failed" && (!includeOffer || sentResult) ? (
          <>
            <Button type="button" variant="secondary" onClick={onClose} disabled={sending}>
              Schließen
            </Button>
            {empfaengerBlockiert ? (
              kundendatenPfad && (
                <ButtonRouterLink to={kundendatenPfad} variant="primary" onClick={onClose}>
                  <Icon name="person" className="text-[16px]" /> Kundendaten prüfen
                </ButtonRouterLink>
              )
            ) : (
              <Button type="button" variant="primary" onClick={() => void handleSend()} disabled={sending}>
                <Icon name="send" className="text-[16px]" /> {sending ? "Sendet …" : "E-Mail erneut senden"}
              </Button>
            )}
          </>
        ) : sendPhase === "compose" ? (
          <>
            {!nurKostenuebernahme && (
              <Button type="button" variant="secondary" className="mr-auto" onClick={() => activeId && onPreview(`/api/events/${eventId}/offers/preview-email`)}>
                <Icon name="visibility" className="text-[16px]" /> Vorschau
              </Button>
            )}
            <Button type="button" variant="secondary" onClick={onClose} disabled={sending}>
              Abbrechen
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => void handleSend()}
              // PR-Review (chatgpt-codex-connector, 27.08.2026): `letterRefineBusy` fehlte hier -
              // ein Klick auf "Jetzt senden" waehrend "Text ueberarbeiten" noch laeuft haette den
              // VOR der Ueberarbeitung stehenden Text verschickt, waehrend die spaeter
              // eintreffende Antwort den (dann schon versendeten) Text im UI ueberschreibt.
              disabled={sending || letterBusy || letterRefineBusy || (costAcceptanceGewuenscht && letterZuLangFuerKostenuebernahme) || versandBlockiert || (datumVergangen && !datumBestaetigt)}
            >
              <Icon name="send" className="text-[16px]" /> {sending ? "Sendet …" : "Jetzt senden"}
            </Button>
          </>
        ) : sendPhase === "sent" && ohneAngebotsseite ? (
          <Button
            type="button"
            variant="primary"
            onClick={() => {
              onClose();
              setSendPhase("compose");
            }}
          >
            Fertig
          </Button>
        ) : (
          sentResult && (
            <>
              {/* F10: zurueck zum Vorgang, aus dem dieses Angebot erzeugt wurde.
                  CX-Audit 2026-07-17: Label hieß „Zur Anfrage", Ziel ist aber das
                  Vorgangs-Detail (/vorgaenge/:id) — Begriff an das Ziel angeglichen. */}
              <Link to={vorgangPfad(eventId!)} className="mr-auto text-[12.5px] font-semibold text-secondary-fixed-dim hover:underline">
                Zum Vorgang
              </Link>
              <Button
                type="button"
                variant="primary"
                onClick={() => {
                  onClose();
                  setSendPhase("compose");
                }}
              >
                Fertig
              </Button>
            </>
          )
        )
      }
    >
      {sendPhase === "mail_failed" && !includeOffer ? (
        // Punkt 9: eigener, minimaler Fehlerzustand fuer den Freitext-Zweig -- ohne
        // publicToken/version gibt es keinen Angebots-Link zum Anzeigen (§ 3.2).
        <div className="space-y-3">
          <p className="text-[12.5px] text-secondary-fixed-dim">
            {customerName ?? "Die Kundin"} hat <strong>keine E-Mail</strong> erhalten.
          </p>
          <p className="w-full rounded-lg bg-error/10 px-3 py-2 text-left text-[11px] font-mono text-error dark:text-[#e39b92]">
            {mailfehlerText(sendMailError)}
          </p>
        </div>
      ) : sendPhase === "mail_failed" && sentResult ? (
        <div className="space-y-3">
          <p className="text-[12.5px] text-secondary-fixed-dim">
            Das Angebot wurde als Version {sentResult.version} versioniert und der öffentliche Link ist gültig –{" "}
            {customerName ?? "die Kundin"} hat aber <strong>keine E-Mail</strong> erhalten.
          </p>
          <p className="w-full rounded-lg bg-error/10 px-3 py-2 text-left text-[11px] font-mono text-error dark:text-[#e39b92]">
            {mailfehlerText(sendMailError)}
          </p>
          <div className="pt-2">
            <div className="flex items-center gap-2 rounded-[9px] border border-outline-variant/40 bg-surface-container-low px-[11px] py-2 dark:border-outline/30 dark:bg-surface-variant/10">
              <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-on-surface-variant">
                {window.location.host}/angebot/{sentResult.publicToken}
              </span>
              <button type="button" className="inline-flex shrink-0 items-center gap-1 text-[12px] font-medium text-primary" onClick={() => void copyPublicLink(sentResult.publicToken)}>
                <Icon name="content_copy" className="text-[13px]" /> {copyDone ? "Kopiert!" : "Kopieren"}
              </button>
            </div>
            <p className="mt-2 text-[11px] text-on-surface-variant/70">
              {empfaengerBlockiert
                ? "Der Link ist gültig – senden Sie ihn manuell, etwa über eine andere Adresse. Ein erneuter E-Mail-Versand an dieselbe Adresse hilft nicht."
                : "Sie können den Link manuell an die Kundin senden – oder den E-Mail-Versand erneut versuchen (erzeugt eine neue Version)."}
            </p>
          </div>
        </div>
      ) : sendPhase === "compose" ? (
          <div className="space-y-3.5">
            {versandWahlSichtbar && (
              <fieldset data-testid="senden-versandart">
                <legend className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Was möchten Sie senden?</legend>
                <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                  {(
                    [
                      ["angebot", "Angebot", true],
                      ["kostenuebernahme", "Kostenübernahme", kostenuebernahmeAlleinMoeglich],
                      ["beides", "Beides", kostenuebernahmeMoeglich],
                    ] as const
                  ).map(([wert, text, moeglich]) => (
                    <label key={wert} className={`flex items-center gap-2 text-[12.5px] text-on-background dark:text-inverse-on-surface ${moeglich ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}>
                      <Radio name="senden-versandart" value={wert} checked={versandArt === wert} disabled={!moeglich} onChange={() => setVersandWahl(wert)} />
                      {text}
                    </label>
                  ))}
                </div>
                <span className="mt-1 block text-[10.5px] text-secondary-fixed-dim">
                  {nurKostenuebernahme
                    ? "Es geht nur die E-Mail mit dem Signatur-Link raus — kein Angebot, keine neue Angebotsversion. Die Nachricht unten steht darin als persönlicher Hinweis."
                    : versandArt === "beides"
                      ? "Zusätzlich zur Angebots-Mail geht eine eigene E-Mail mit dem Signatur-Link raus — die Nachricht unten wird darin als persönlicher Hinweis mitgeschickt."
                      : "Nur die Angebots-Mail, ohne Kostenübernahme."}
                </span>
              </fieldset>
            )}

            {/* Empfänger */}
            <div>
              <label className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Empfänger</label>
              <div className="flex items-center gap-2.5 rounded-[10px] border border-outline-variant/60 bg-surface-container-lowest dark:bg-night-card px-[11px] py-2 dark:border-outline/40">
                <span className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-full bg-primary/14 text-[11px] font-semibold text-primary">
                  {initials(customerName)}
                </span>
                <span className="truncate text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">{customerName ?? "Kunde"}</span>
                <span className="truncate text-[12px] text-secondary-fixed-dim">{customerEmail ?? "keine E-Mail hinterlegt"}</span>
              </div>
              {companyEmail && !nurKostenuebernahme && (
                <label className="mt-1.5 flex items-center gap-2 text-[12px] text-on-surface-variant">
                  <Checkbox checked={ccSelf} onChange={(e) => setCcSelf(e.target.checked)} />
                  Kopie an mich senden ({companyEmail})
                </label>
              )}
            </div>

            {/* Betreff */}
            {!nurKostenuebernahme && (
            <label className="block">
              <span className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Betreff</span>
              <Input
                className="!py-2.5"
                data-testid="senden-dialog-betreff"
                value={subject}
                onChange={(e) => {
                  // Punkt (b): ab hier ist der Betreff Handarbeit -- der Checkbox-Effekt oben
                  // ueberschreibt ihn nicht mehr.
                  subjectAutomatisch.current = false;
                  onSubjectChange(e.target.value);
                }}
                placeholder="Ihr Angebot …"
              />
            </label>
            )}

            {/* Nachricht -- Compose-Feld je Modus, TextbausteinPicker (§ 3.3, § 4.1) in BEIDEN
                Modi gleich eingebunden, direkt darueber. */}
            {nachtrag ? (
              <p role="note" data-testid="nachtrag-ohne-anschreiben" className="rounded-[10px] border border-outline-variant/60 bg-surface-container-low px-3 py-2 text-[12.5px] text-on-surface-variant dark:border-outline/40">
                Zu einem Nachtrag gehört kein Anschreiben. Die Mail nennt die Änderung und führt die Kundin zum Vergleich mit ihrer bisherigen Buchung.
              </p>
            ) : (
            <div>
              {/* Mockup Variante B „KI-Menü“ (Antoine, 25.10.2026): links „Nachricht“, rechts genau
                  zwei gleich gestaltete ruhige Knöpfe. Der Picker-Popup haftet an dieser Zeile
                  (`relative`), damit er auch am Handy nicht über den Rand ragt. */}
              <div className="relative mb-1.5 flex min-h-[30px] items-center justify-between gap-2">
                <span className="block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">
                  {nurKostenuebernahme ? "Persönlicher Hinweis" : "Nachricht"}
                </span>
                <div className="flex items-center gap-1.5">
                  <TextbausteinPicker
                    ruhig
                    onInsert={(content) =>
                      includeOffer || nurKostenuebernahme
                        ? insertAtCursor(letterTextareaRef.current, letter, onLetterChange, content)
                        : insertAtCursor(freetextTextareaRef.current, freetextText, setFreetextText, content)
                    }
                  />
                  {/* KI-Menü immer da (Antoine, 25.10.2026) */}
                    <Aufklapper
                      className="w-60"
                      ausloeser={({ offen, umschalten }) => (
                        <button
                          type="button"
                          aria-haspopup="menu"
                          aria-expanded={offen}
                          aria-label={kiBusyText ?? "KI"}
                          disabled={kiBusy}
                          onClick={umschalten}
                          className={`${RUHIGER_KNOPF} ${RUHIGER_KNOPF_KI}`}
                        >
                          {kiBusy ? (
                            <Icon name="progress_activity" className="animate-spin text-[16px]" />
                          ) : (
                            <Icon name="auto_awesome" className="text-[16px]" />
                          )}
                          {kiBusyText ?? "KI"}
                          {!kiBusy && <Icon name="expand_more" className="text-[16px]" />}
                        </button>
                      )}
                    >
                      {(schliessen) => (
                        <>
                          {elternRefine && (
                            <MenueEintrag
                              icon="auto_awesome"
                              aria-label="Anschreiben erstellen"
                              disabled={letterBusy || letterRefineBusy}
                              onClick={() => {
                                schliessen();
                                neuFormulieren();
                              }}
                            >
                              <b className="block text-[12.5px] font-semibold">Anschreiben erstellen</b>
                              <small className="block text-[11px] text-on-surface-variant">Passend zum aktuellen Angebot</small>
                            </MenueEintrag>
                          )}
                          {/* Punkt 7: LEICHTE Korrektur (Rechtschreibung/kleine Formulierung) des
                              vorhandenen Textes - Gegenstueck zu "Neu formulieren" (komplette
                              Neugenerierung). Ohne Text nichts zu ueberarbeiten -> disabled. */}
                          <MenueEintrag
                            icon="spellcheck"
                            aria-label="Text überarbeiten"
                            title={feldText.trim() ? undefined : "Erst Text schreiben, dann überarbeiten"}
                            disabled={kiBusy || !feldText.trim()}
                            onClick={() => {
                              schliessen();
                              void textUeberarbeiten();
                            }}
                          >
                            <b className="block text-[12.5px] font-semibold">Text überarbeiten</b>
                            <small className="block text-[11px] text-on-surface-variant">Rechtschreibung und Feinschliff</small>
                          </MenueEintrag>
                          {elternRefine && (
                            <MenueEintrag
                              icon="autorenew"
                              aria-label="Neu formulieren"
                              disabled={letterBusy || letterRefineBusy}
                              onClick={() => {
                                schliessen();
                                neuFormulieren();
                              }}
                            >
                              <b className="block text-[12.5px] font-semibold">Neu formulieren</b>
                              <small className="block text-[11px] text-on-surface-variant">Komplett neuer Text</small>
                            </MenueEintrag>
                          )}
                        </>
                      )}
                    </Aufklapper>
                </div>
              </div>
              {refineLokalFehler && (
                <p role="alert" className="mb-1.5 text-[11.5px] text-error">
                  {refineLokalFehler}
                </p>
              )}
              {/* Hinweisleiste: haftet am oberen Rand des Textfelds und teilt dessen Rahmen. */}
              {includeOffer && vorherigesAnschreiben !== null && (
                <div
                  data-testid="anschreiben-hinweisleiste"
                  className="flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-t-lg border border-b-0 border-outline-variant bg-primary/8 px-2.5 py-1.5 text-[11.5px] text-on-background dark:text-inverse-on-surface"
                >
                  <Icon name="info" className="text-[15px] text-primary" />
                  <span className="min-w-0 flex-1">Anschreiben neu erstellt, weil sich das Angebot geändert hat.</span>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 whitespace-nowrap rounded-md px-1 py-0.5 font-bold text-primary hover:underline max-sm:ml-[23px]"
                    onClick={onVorherigesAnschreibenWiederherstellen}
                  >
                    <Icon name="undo" className="text-[14px]" />
                    Vorherigen Text wiederherstellen
                  </button>
                </div>
              )}
              {includeOffer || nurKostenuebernahme ? (
                letterBusy && !letter ? (
                  <div className="min-h-[140px] animate-pulse rounded-lg bg-surface-container-high/40 dark:bg-surface-variant/20" />
                ) : (
                  <Textarea
                    ref={letterTextareaRef}
                    className={`min-h-[140px] text-[12.5px] leading-relaxed${includeOffer && vorherigesAnschreiben !== null ? " !rounded-t-none" : ""}`}
                    value={letter}
                    onChange={(e) => onLetterChange(e.target.value)}
                    placeholder="Sehr geehrte Damen und Herren, …"
                  />
                )
              ) : (
                <Textarea
                  ref={freetextTextareaRef}
                  className="min-h-[140px] text-[12.5px] leading-relaxed"
                  value={freetextText}
                  onChange={(e) => setFreetextText(e.target.value)}
                  placeholder="Ihre Nachricht an die Kundin …"
                />
              )}
              {includeOffer && auswahlHinweis !== null && (
                <p role="note" className="mt-1.5 text-[11.5px] leading-snug text-on-surface-variant">
                  {auswahlHinweis === "auto"
                    ? "Die Variantenauswahl hat sich geändert — der Text wird neu formuliert."
                    : "Die Variantenauswahl hat sich geändert — bitte prüfen oder „Neu formulieren“ wählen."}
                </p>
              )}
              {includeOffer && !nurKostenuebernahme && nenntKonditionen(letter) && (
                <p role="note" className="mt-1.5 text-[11.5px] leading-snug text-on-surface-variant">
                  Der Text nennt Zahlungs- oder Stornobedingungen. Verbindlich sind die Konditionen im Angebot — bitte
                  prüfen, ob der Text dazu passt, oder „Neu formulieren“ wählen.
                </p>
              )}
            </div>
            )}

            {/* Angebot anfuegen (22.08.2026) -- ersetzt den frueheren Versandmodus-Umschalter.
                Standard-CRM-Muster: eine Checkbox am Anhang statt zweier abstrakter Modi. Sie
                steuert zugleich den Versandweg in `sendOffer()` (Varianten- vs. Freitext-Pfad)
                und welches Textfeld gezeigt wird (`letter` vs. `freetextText`). */}
            {!nurKostenuebernahme && (
            <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-outline-variant/40 bg-surface-container-low px-3 py-2.5 hover:bg-surface-container-high/30 dark:border-outline/30 dark:bg-surface-variant/10">
              <Checkbox
                checked={includeOffer}
                onChange={(e) => setIncludeOffer(e.target.checked)}
                className="mt-0.5 shrink-0"
              />
              {/* B5 (UX-Polish 25.08.2026): Hier stand „Aktuelles Angebot als Link und PDF
                  anfügen" — und das war schlicht unwahr. KEINE der 13 `mailer.send`-Aufrufstellen
                  uebergibt `attachments`, ein serverseitiges PDF gibt es gar nicht
                  (`GET /api/offers/:id/document` liefert druckfertiges HTML). Die Mail traegt
                  einen LINK, sonst nichts. Ein Versprechen, das der Betreiber der Kundin
                  weitergibt („das PDF ist im Anhang"), ist teurer als eine nuechterne Zeile —
                  darum steht jetzt da, was tatsaechlich passiert, samt dem Weg zum Ausdruck. */}
              <span>
                <span className="block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">
                  Aktuelles Angebot als Link mitschicken
                </span>
                <span className="block text-[10.5px] text-secondary-fixed-dim">
                  Die Kundin öffnet ihre Angebotsseite im Browser und kann sie dort ausdrucken oder als PDF sichern.
                  Es wird <strong>kein Dokument angehängt</strong>. Ohne Haken geht eine reine Nachricht raus — ohne
                  Angebotsseite.
                </span>
              </span>
            </label>
            )}

            {/* Live-Vorschau -- nur wenn das Angebot mitgeschickt wird. Antoine (01.09.2026):
                gehoert unmittelbar zu "Aktuelles Angebot als Link mitschicken" (der Haken direkt
                darueber) - stand vorher hinter "Kostenübernahme mit anfordern" und riss damit die
                Vorschau von dem Haken los, den sie erklaert. */}
            {/* KÜ V2 (S3): gilt eine unterschriebene Kostenübernahme, zeigt die Preisvorschau den
                Unterschied (alt / neu / Differenz / bereits bezahlt) — nur mit gültiger KÜ sichtbar. */}
            {eventId && <KueDifferenz eventId={eventId} optionId={activeId} />}
            {includeOffer && !nurKostenuebernahme && (
              <div className="space-y-2.5 rounded-xl border border-outline-variant/40 bg-surface-container-low p-3 dark:border-outline/30 dark:bg-surface-variant/10">
                <p className="text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">
                  Das sieht die Kundin <span className="font-normal text-secondary-fixed-dim">· wählt eine Option online</span>
                </p>
                {/* B3 (UX-Polish 25.08.2026, selektiver Versand): Bis hierher ging IMMER alles
                    raus, was gerade aktiv war. Wer eine dritte Variante nur zum Durchrechnen
                    gebaut hatte, musste sie vor dem Versand loeschen — und danach neu bauen,
                    wenn die Kundin doch danach fragte. Der Haken je Karte nimmt sie einfach vom
                    Versand aus; abgewaehlt heisst `is_active = false`, also derselbe
                    Soft-Delete-Weg, den der Muelleimer in der Varianten-Leiste geht.
                    Die Abwahl wirkt erst beim Senden (siehe `SendPayload.deaktivierteOptionIds`)
                    und ist bis dahin ein Klick weit zurueckzunehmen. */}
                <div className="grid grid-cols-3 gap-2">
                  {options.map((o) => {
                    const isActive = o.id === activeId;
                    /**
                     * Der Betrag der GERADE BEARBEITETEN Variante kommt live aus dem Editor, alle
                     * anderen aus der Liste — exakt dieselbe Unterscheidung wie in
                     * `VariantenLeiste.tsx` (`isActive ? breakdown.amountTotalCents : o.…`), kein
                     * zweiter Rechenweg.
                     *
                     * Bis zum 17.09.2026 stand hier nur `o.amountTotalCents`. `options` kommt aus
                     * `GET /api/events/:id/offers` und wird beim OEFFNEN der Seite geladen; keine
                     * Preisaenderung danach erreichte sie. Der Betreiber sah deshalb auf EINEM
                     * Bildschirm gleichzeitig 345,20 EUR/Gast (Zusammenfassung) und 202,70 EUR/Gast
                     * (hier) — nachgemessen in Produktion: der Server hatte 690,40 EUR gespeichert,
                     * die Zusammenfassung stimmte, dieser Dialog zeigte den Stand vom Seitenaufruf.
                     *
                     * Das ist mehr als eine Anzeigefrage: Dies ist der Ort, an dem verbindlich
                     * versendet wird. Wer hier die falsche Zahl liest und „Jetzt senden" drueckt,
                     * hat einem anderen Preis zugestimmt als dem, der hinausgeht.
                     */
                    const totalCents = isActive && aktiverBetragCents != null ? aktiverBetragCents : o.amountTotalCents;
                    const perGuest = o.guestCount > 0 ? Math.round(totalCents / o.guestCount) : totalCents;
                    const raus = abgewaehlt.has(o.id);
                    const sperrGrund = abwahlGesperrtGrund(o);
                    return (
                      <div
                        key={o.id}
                        data-testid={`senden-variante-${o.id}`}
                        className={`relative rounded-lg p-2 transition-opacity ${raus ? "opacity-45" : ""} ${
                          isActive && !raus
                            ? "border-[1.5px] border-primary bg-primary/6"
                            : "border border-outline-variant/50 bg-surface-container-lowest dark:bg-night-card dark:border-outline/30"
                        }`}
                      >
                        {isActive && !raus && (
                          <span className="absolute -top-2 right-2 rounded-full bg-primary px-1.5 py-0.5 text-[8.5px] font-bold uppercase tracking-wide text-on-primary">
                            Empfohlen
                          </span>
                        )}
                        <label className={`flex items-start gap-1.5 ${sperrGrund ? "cursor-not-allowed" : "cursor-pointer"}`} title={sperrGrund ?? undefined}>
                          <Checkbox
                            data-testid={`senden-variante-haken-${o.id}`}
                            className="mt-0.5 shrink-0"
                            checked={!raus}
                            disabled={sperrGrund != null}
                            aria-label={`Variante ${o.title || (o.aLaCarte ? NACH_KARTE : money(o.amountTotalCents))} mitschicken`}
                            onChange={(e) =>
                              setAbgewaehlt((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.delete(o.id);
                                else next.add(o.id);
                                return next;
                              })
                            }
                          />
                          <span className="min-w-0">
                            {/* Kein Buchstaben-Kicker mehr (Antoine, 11.09.2026) - nur noch Preis-
                                Sortierung, ohne eigenen Titel identifiziert der Preis die Variante. */}
                            <span className="block truncate text-[11.5px] font-semibold text-on-background dark:text-inverse-on-surface">
                              {o.title || (o.aLaCarte ? NACH_KARTE : money(o.amountTotalCents))}
                            </span>
                            <span className={`block text-[12.5px] font-bold ${isActive && !raus ? "text-primary" : "text-on-background dark:text-inverse-on-surface"}`}>
                              {/* Migration 731: „À la carte“ hat keinen Betrag - der Dialog sagt es, statt „0,00 €“ zu zeigen. */}
                              {o.aLaCarte ? (
                                <span data-testid={`senden-variante-a-la-carte-${o.id}`}>À la carte</span>
                              ) : totalCents === 0 ? (
                                /* sql/763: Variante nur aus Zubuchungen - der Gast wählt per Zähler; kein „0,00 € /Gast“. */
                                <span data-testid={`senden-variante-nach-auswahl-${o.id}`}>nach Auswahl</span>
                              ) : (
                                <>
                                  {money(perGuest)} <span className="text-[9.5px] font-medium text-secondary-fixed-dim">/Gast</span>
                                </>
                              )}
                            </span>
                          </span>
                        </label>
                      </div>
                    );
                  })}
                </div>
                {/* Der Preis der Abwahl steht dabei, bevor sie wirkt: sie ist beim Senden
                    endgueltig (`is_active = false`), nicht nur „diesmal nicht mitschicken". */}
                {abgewaehlt.size > 0 && (
                  <p data-testid="senden-abwahl-hinweis" className="text-[10.5px] text-secondary-fixed-dim">
                    {abgewaehlt.size === 1 ? "Eine Variante geht nicht mit" : `${abgewaehlt.size} Varianten gehen nicht mit`} — sie
                    wird beim Senden deaktiviert und taucht danach nicht mehr im Builder auf.
                  </p>
                )}
                <div className="flex items-center gap-2 rounded-[9px] border border-outline-variant/40 bg-surface-container-lowest dark:bg-night-card px-[11px] py-2 dark:border-outline/30">
                  <Icon name="public" className="text-[14px] text-primary" />
                  {/* B5 (UX-Polish 25.08.2026): Der Platzhalter „Link wird beim Senden erzeugt"
                      ist entfallen. Er beschrieb einen technischen Zwischenzustand, der den
                      Betreiber nichts angeht — und stand ausgerechnet dort, wo er die Zeile
                      brauchte: der Knopf daneben zeigt SCHON JETZT dieselbe Seite, die der Gast
                      spaeter sieht. Existiert der Token bereits (erneuter Versand), steht die
                      echte Adresse da; sonst traegt die Zeile allein den Weg zur Vorschau.
                      Weiterhin KEIN toter Link auf /angebot/… vor dem Versand. */}
                  {eventPublicToken ? (
                    <span className="truncate font-mono text-[11px] text-on-surface-variant">
                      {window.location.host}/angebot/{eventPublicToken}
                    </span>
                  ) : (
                    <span className="truncate text-[11px] text-on-surface-variant">Angebotsseite der Kundin</span>
                  )}
                  <Link
                    to={angebotsseitePfad(eventId!)}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-auto shrink-0 text-[11px] font-semibold text-primary hover:underline"
                    title="So sieht der Gast die Angebotsseite — auch vor dem Versand"
                  >
                    Kundenseite ansehen
                  </Link>
                </div>
              </div>
            )}

            {/* Punkt 8 (Bughunt 27.08.2026): "Kostenübernahme mit anfordern" - EIN zusaetzlicher,
                unabhaengiger Versand (eigene Mail, eigene, bereits gehaertete Route), kein
                zusammengelegtes Dokument. Nur sichtbar, wenn ueberhaupt eine eindeutige
                Vertragsgrundlage mitgeht (s. `kostenuebernahmeMoeglich` oben) - sonst wuerde der
                Haken nur in einen 422 des Servers laufen, den der Betreiber schon geglaubt hat.
                Antoine (01.09.2026): steht jetzt NACH der Vorschau statt dazwischen - sie gehoert
                zum Haken direkt darueber ("Aktuelles Angebot als Link mitschicken"), nicht zu
                diesem hier. */}
            {costAcceptanceGewuenscht && (
              <>
              {letterZuLangFuerKostenuebernahme && (
                <p className="text-[10.5px] font-semibold text-error dark:text-[#e39b92]">
                  Nachricht ist zu lang für die Kostenübernahme (max. {KOSTENUEBERNAHME_NOTE_MAX} Zeichen, aktuell{" "}
                  {letter.trim().length}). Bitte kürzen oder „Angebot“ wählen.
                </p>
              )}
              {costAcceptanceGewuenscht && kundentypUnbestaetigt && (
                <div data-testid="kundentyp-nachfrage" className="-mt-1 flex flex-col gap-2 rounded-lg border border-outline-variant bg-[#F0EAE0] p-2.5 dark:border-white/10 dark:bg-white/[0.08]">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-[11.5px] font-semibold text-on-background dark:text-inverse-on-surface">Welcher Kundentyp ist das?</span>
                    <AutomatischErkanntChip text={`erkannt: ${KUNDENTYP_TEXT[kundentypUnbestaetigt]}`} />
                  </span>
                  <KundentypAuswahl wert={kundentypWahl ?? kundentypUnbestaetigt} onWahl={setKundentypWahl} disabled={sending} testId="kundentyp-nachfrage-auswahl" />
                  <span className="text-[10.5px] text-secondary-fixed-dim">
                    Der Typ bestimmt die Vertragsvorlage und wird beim Kunden gespeichert — er gilt dann für alle seine Aufträge.
                  </span>
                </div>
              )}
              </>
            )}

            {/* Angebotsdokument (Vorschau) + Gültig bis */}
            <div className="flex flex-wrap items-end justify-between gap-3.5">
              {includeOffer && !nurKostenuebernahme && (
                <div>
                  {/* B5: Diese Ueberschrift hiess „Anhang" — der zweite Ort, an dem der Dialog
                      einen Mail-Anhang behauptete, den es nicht gibt. Der Knopf oeffnet die
                      Druckansicht FUER DEN BETREIBER (`GET /api/offers/:id/document`, HTML), er
                      haengt nichts an eine Mail. Ueberschrift und Unterzeile sagen das jetzt. */}
                  <span className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">
                    Angebotsdokument prüfen
                  </span>
                  <button
                    type="button"
                    className="flex items-center gap-2.5 rounded-[10px] border border-outline-variant/40 bg-surface-container-lowest dark:bg-night-card px-3 py-2 text-left hover:bg-surface-container-high/30 dark:border-outline/30"
                    onClick={() => activeId && onPreview(`/api/offers/${activeId}/document`)}
                    disabled={!activeId}
                  >
                    <span className="grid h-8 w-8 place-items-center rounded-lg bg-surface-container-high text-on-surface-variant dark:bg-white/[0.08]">
                      <Icon name="description" className="text-[18px]" />
                    </span>
                    <span>
                      <span className="block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Druckansicht öffnen</span>
                      <span className="block text-[10.5px] text-secondary-fixed-dim">Nur für Sie — wird der Mail nicht angehängt</span>
                    </span>
                  </button>
                </div>
              )}
              <label className="block">
                <span className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Gültig bis</span>
                <DateInput
                  inputClassName="!py-2"
                  defaultValue={eventOfferValidUntil ?? ""}
                  key={eventOfferValidUntil ?? ""}
                  onCommit={(v) => onPatchValidUntil(v)}
                />
              </label>
            </div>

            {/* B1: die offenen Punkte, unmittelbar ueber den Knoepfen. Bewusst ein HINWEIS und
                keine Sperre — „Jetzt senden" bleibt anklickbar. Dieselbe Haltung wie ueberall
                sonst im Versandweg (`VorDemSendenKarte.tsx`, der CTA-Zaehler): es gibt gute
                Gruende, mit offenen Punkten zu senden, und der Betreiber kennt sie besser als
                die Software. Was er nicht darf, ist es aus Versehen tun. */}
            {datumVergangen && (
              <DatumVergangenHinweis
                datumIso={eventDateIso}
                endeIso={eventDateEndIso}
                testId="senden-datum-vergangen"
                onKorrigieren={onPatchEventDate ? (iso, ende) => void korrigiereDatum(iso, ende) : undefined}
                bestaetigt={datumBestaetigt}
                onBestaetigt={(ja) => setDatumBestaetigtFuer(ja ? datumSchluessel : null)}
              />
            )}
            {offenePunkte.length > 0 && (
              <div
                data-testid="senden-dialog-offene-punkte"
                className="flex items-start gap-2.5 rounded-xl border border-secondary/40 bg-[#F0EAE0] px-3 py-2.5 dark:border-white/10 dark:bg-white/[0.08]"
              >
                <Icon name="pending" className="mt-0.5 shrink-0 text-[16px] text-secondary dark:text-[#b8ae9e]" />
                <div className="min-w-0">
                  <p className="text-[12px] font-semibold text-secondary dark:text-[#b8ae9e]">
                    {offenePunkte.length === 1 ? "Ein Punkt ist noch offen" : `${offenePunkte.length} Punkte sind noch offen`}
                  </p>
                  <p className="mt-0.5 text-[11px] text-secondary/80 dark:text-[#b8ae9e]/80">{offenePunkte.join(" · ")}</p>
                  <p className="mt-1 text-[10.5px] text-secondary/70 dark:text-[#b8ae9e]/70">
                    Senden ist trotzdem möglich — schließen Sie den Dialog, wenn Sie das vorher klären möchten.
                  </p>
                </div>
              </div>
            )}

            {versandBlockiert && freischaltenKarte}
            {sperre && (
              <SperreVorOrt
                sperre={sperre}
                testId="senden-dialog-sperre"
                onNeuLaden={() => {
                  onSperreNeuLaden?.();
                  onClose();
                }}
                onNeuSenden={() => void handleSend()}
              />
            )}
            {error && <ErrorNote message={error} />}
            {nurKostenuebernahme && caOutcome && !caOutcome.ok && <ErrorNote message={`Kostenübernahme-Anforderung fehlgeschlagen: ${caOutcome.message ?? "unbekannter Fehler"}`} />}
          </div>
      ) : sendPhase === "sent" && nurKostenuebernahme ? (
        <div className="flex items-start gap-2 rounded-lg bg-[#F0EAE0] px-3 py-2 text-[11.5px] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]">
          <Icon name="task_alt" className="mt-px shrink-0 text-[14px]" />
          <span>
            {customerName ?? "Die Kundin"} erhält die Kostenübernahme mit dem Signatur-Link per E-Mail.{" "}
            {eventId && (
              <Link to={abrechnungPfad(eventId)} className="font-semibold underline underline-offset-2">
                Zur Abrechnung
              </Link>
            )}
          </span>
        </div>
      ) : sendPhase === "sent" && !includeOffer ? (
        // Punkt 9: schlichte Erfolgsanzeige ohne Link-Aktionen -- eine Freitext-Mail erzeugt
        // keine Angebotsversion und keine oeffentliche Angebotsseite (§ 3.2).
        <div>
          <p className="text-[12.5px] text-secondary-fixed-dim">{customerName ?? "Die Kundin"} wurde per E-Mail benachrichtigt.</p>
          {freetextSentAt && (
            <div className="space-y-[9px] pt-[18px]">
              <Row label="Empfänger" value={customerName ?? "–"} />
              <Row
                label="Gesendet"
                value={new Date(freetextSentAt).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              />
            </div>
          )}
        </div>
      ) : (
        sentResult && (
          <div>
            <p className="text-[12.5px] text-secondary-fixed-dim">
              {customerName ?? "Die Kundin"} wurde per E-Mail benachrichtigt und kann ihre Wunschoption jetzt online wählen.
            </p>
            <div className="space-y-[9px] pt-[18px]">
              <Row label="Empfänger" value={customerName ?? "–"} />
              <Row label="Gesendet" value={new Date(sentResult.sentAt).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })} />
              <Row label="Gültig bis" value={eventOfferValidUntil ? formatDate(eventOfferValidUntil) : "–"} />
              <div className="flex items-center justify-between">
                <dt className="text-on-surface-variant">Status</dt>
                <dd className="inline-flex items-center gap-1.5 rounded-full bg-[#F0EAE0] px-2.5 py-0.5 text-[11px] font-semibold text-[#6F675A]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#6F675A]" /> Angebot gesendet
                </dd>
              </div>
            </div>
            {/* Punkt 8: Ergebnis des zweiten, unabhaengigen Requests - ein Fehlschlag hier
                verdeckt NICHT den Erfolg des Angebots-Versands oben, sondern steht als eigener,
                sichtbarer Hinweis daneben (kein stiller Fehlschlag). */}
            {caOutcome && (
              <div className="pt-2">
                {caOutcome.ok ? (
                  <div className="flex items-start gap-2 rounded-lg bg-[#F0EAE0] px-3 py-2 text-[11.5px] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]">
                    <Icon name="task_alt" className="mt-px shrink-0 text-[14px]" />
                    <span>
                      Kostenübernahme ebenfalls angefordert.{" "}
                      {eventId && (
                        <Link to={abrechnungPfad(eventId)} className="font-semibold underline underline-offset-2">
                          Zur Abrechnung
                        </Link>
                      )}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 rounded-lg bg-error/10 px-3 py-2 text-[11.5px] text-error dark:text-[#e39b92]">
                    <Icon name="error" className="mt-px shrink-0 text-[14px]" />
                    <span>
                      Kostenübernahme-Anforderung fehlgeschlagen: {caOutcome.message ?? "unbekannter Fehler"}. Das Angebot wurde
                      trotzdem gesendet — bitte{" "}
                      {eventId && (
                        <Link to={abrechnungPfad(eventId)} className="font-semibold underline underline-offset-2">
                          auf der Abrechnungsseite
                        </Link>
                      )}{" "}
                      erneut anfordern.
                    </span>
                  </div>
                )}
              </div>
            )}
            <div className="pt-2">
              <div className="flex items-center gap-2 rounded-[9px] border border-outline-variant/40 bg-surface-container-low px-[11px] py-2 dark:border-outline/30 dark:bg-surface-variant/10">
                <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-on-surface-variant">
                  {window.location.host}/angebot/{sentResult.publicToken}
                </span>
                <button type="button" className="inline-flex shrink-0 items-center gap-1 text-[12px] font-medium text-primary" onClick={() => void copyPublicLink(sentResult.publicToken)}>
                  <Icon name="content_copy" className="text-[13px]" /> {copyDone ? "Kopiert!" : "Kopieren"}
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2.5 pt-3">
              <Button type="button" variant="secondary" onClick={() => eventId && onPreview(`/api/events/${eventId}/offers/preview-email`)}>
                <Icon name="mail" className="text-[14px]" /> E-Mail ansehen
              </Button>
              <Button type="button" variant="secondary" onClick={() => window.open(`${window.location.origin}/angebot/${sentResult.publicToken}`, "_blank")}>
                <Icon name="public" className="text-[14px]" /> Öffentliche Seite
              </Button>
            </div>
          </div>
        )
      )}
    </Modal>
    <UnzustellbarDialog
      info={unzustellbar}
      onClose={() => setUnzustellbar(null)}
      onSenden={async (optionen) => {
        await sendeAngebot(optionen, true);
        setUnzustellbar(null);
      }}
    />
    </>
  );
}
